import React, { useState } from 'react';
import { Download, Eye, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, SectionTitle, ghostBtn, goldBtn } from '../ui';
import { api, downloadFile, eur, errMsg, fmtDateTime } from '../api';
import { usePortal } from '../PortalAuth';
import NegativeDetails from './NegativeDetails';

const FLAGS = [['protesti', 'Protesti'], ['pregiudizievoli', 'Pregiudizievoli'], ['procedure', 'Procedure concorsuali'], ['coinvoltoInProcedura', 'Coinvolto in procedure']];

function Outcome({ r }) {
  if (r.status === 'pending') return <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border rounded-sm text-[#D4AF37] border-[#5A4A17]">In corso</span>;
  if (r.status === 'error') return <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border rounded-sm text-[#E37A72] border-[#5A2420]">Non riuscita · rimborsata</span>;
  return r.positive
    ? <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border rounded-sm text-[#E37A72] border-[#5A2420]">Esito positivo</span>
    : <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border rounded-sm text-green-400 border-green-900">Nessuna negatività</span>;
}

function Row({ r, onUpdate, detailPrice }) {
  const { refresh } = usePortal();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const openDetails = async () => {
    if (r.details) { setOpen((o) => !o); return; }
    if (!window.confirm(`Aprire il dettaglio delle negatività di ${r.name}?${detailPrice ? ` Costo ${eur(detailPrice)}.` : ''}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/negative/requests/${r.id}/details`);
      onUpdate(data); setOpen(true); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const Icon = r.positive ? ShieldAlert : ShieldCheck;
  return (
    <Card className="p-5" data-testid={`negative-row-${r.id}`}>
      <div className="flex flex-wrap items-center gap-4">
        <Icon size={18} className={r.positive ? 'text-[#E37A72] shrink-0' : 'text-[#D4AF37] shrink-0'} />
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm text-[#F2F2F2] truncate">{r.name}</p>
          <p className="text-xs font-mono text-[#5A5F67]">{r.tax_code} · {fmtDateTime(r.created_at)}{r.source === 'aml' ? ' · inclusa nel reputational check' : ''}</p>
        </div>
        <span data-testid={`negative-outcome-${r.id}`}><Outcome r={r} /></span>
        {r.status === 'done' && r.positive && (
          <button onClick={openDetails} disabled={busy} className={`${r.details ? ghostBtn : goldBtn} h-9 px-3 inline-flex items-center gap-2 text-xs`} data-testid={`negative-details-btn-${r.id}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            {r.details ? (open ? 'Chiudi dettaglio' : 'Vedi dettaglio') : `Apri dettaglio${detailPrice ? ` · ${eur(detailPrice)}` : ''}`}
          </button>
        )}
        {r.status === 'done' && (
          <button onClick={() => downloadFile(`/negative/requests/${r.id}/pdf`, `BOND_negativita_${r.tax_code}.pdf`)} className={`${ghostBtn} h-9 px-3 inline-flex items-center gap-2 text-xs`} data-testid={`negative-pdf-btn-${r.id}`}>
            <Download size={14} />PDF
          </button>
        )}
      </div>
      {r.status === 'done' && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid={`negative-flags-${r.id}`}>
          {FLAGS.map(([k, l]) => (
            <span key={k} className={`text-[11px] px-2 py-0.5 border rounded-sm ${r.flags?.[k] ? 'border-[#5A2420] text-[#E37A72]' : 'border-[#1F242C] text-[#5A5F67]'}`}>{l}: {r.flags?.[k] ? 'presenti' : 'no'}</span>
          ))}
        </div>
      )}
      {r.status === 'error' && r.message && <p className="text-xs text-[#8A8F97] mt-2">{r.message}</p>}
      {open && r.details && <NegativeDetails d={r.details} id={r.id} />}
    </Card>
  );
}

export default function NegativeList({ items, setItems, detailPrice }) {
  const update = (doc) => setItems((l) => l.map((x) => (x.id === doc.id ? doc : x)));
  return (
    <div data-testid="negative-list">
      <SectionTitle>Le mie ricerche</SectionTitle>
      {items === null && <Loader2 size={18} className="animate-spin text-[#D4AF37]" />}
      {items?.length === 0 && <p className="text-sm text-[#5A5F67]" data-testid="negative-list-empty">Nessuna ricerca eseguita finora.</p>}
      <div className="space-y-3">{(items || []).map((r) => <Row key={r.id} r={r} onUpdate={update} detailPrice={detailPrice} />)}</div>
    </div>
  );
}
